import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  inject,
  Input,
  OnDestroy,
  QueryList,
  TemplateRef
} from '@angular/core';
import { GridTileItemDirective } from '@directives';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'grid-panel-container',
  templateUrl: './grid-panel-container.component.html',
  styleUrls: ['./grid-panel-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GridPanelContainerComponent implements AfterContentInit, OnDestroy {
  @Input() cols = 2;
  @Input() rowHeight = '200px';
  @Input() gutterSize = '1px';

  @ContentChildren(GridTileItemDirective, { read: TemplateRef })
  private contentItems!: QueryList<TemplateRef<any>>;

  @ContentChildren(GridTileItemDirective)
  private tileDirectives!: QueryList<GridTileItemDirective>;

  gridItems: {
    templateRef: TemplateRef<any>;
    colspan: number;
    rowspan: number;
  }[] = [];

  private destroy$ = new Subject<void>();
  private cdr = inject(ChangeDetectorRef);

  ngAfterContentInit(): void {
    this.updateGridItems();

    // Listen to changes in content children
    this.contentItems.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateGridItems());

    this.tileDirectives.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateGridItems());

    this.startPropertyChangeMonitoring();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateGridItems(): void {
    const newGridItems = this.contentItems.map((templateRef, index) => {
      const directive = this.tileDirectives.toArray()[index];
      return {
        templateRef,
        colspan: directive?.colspan ?? 1,
        rowspan: directive?.rowspan ?? 1
      };
    });

    const hasChanged =
      this.gridItems.length !== newGridItems.length ||
      this.gridItems.some(
        (item, index) =>
          item.colspan !== newGridItems[index]?.colspan ||
          item.rowspan !== newGridItems[index]?.rowspan
      );

    if (hasChanged) {
      this.gridItems = newGridItems;
      this.cdr.detectChanges();
    }
  }

  private startPropertyChangeMonitoring(): void {
    const checkForUpdates = () => {
      if (this.tileDirectives && this.contentItems) {
        this.updateGridItems();
      }

      if (!this.destroy$.closed) {
        requestAnimationFrame(checkForUpdates);
      }
    };

    requestAnimationFrame(checkForUpdates);
  }

  trackByFn(index: number): number {
    return index;
  }
}
